import { Eye, EyeOff, Globe, Lock, MessageSquare, Plus } from 'lucide-react';
import { useState } from 'react';
import { createFileRoute, Link, useParams } from '@tanstack/react-router';

import { useInquiries, useCreateInquiry, TYPE_CONFIG, STATUS_CONFIG } from '@/features/support';
import { Badge } from '@/shared/ui/badge';
import { Button } from '@/shared/ui/button';
import { Card, CardContent } from '@/shared/ui/card';
import { DataTablePagination } from '@/shared/ui/data-table-pagination';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/shared/ui/dialog';
import { Input } from '@/shared/ui/input';
import { Label } from '@/shared/ui/label';
import { Skeleton } from '@/shared/ui/skeleton';
import { PageHeader } from '@/shared/ui/page-header';
import { AnimatedContainer } from '@/shared/ui/animated';
import { AppLayout } from '../-components/app-layout';

import type { InquiryStatus, InquiryType } from '@cafe24-apps/traffic-light-server/contracts';

export const Route = createFileRoute('/_authenticated/$mallId/support/')({
  component: SupportListPage,
});

function TypeBadge({ type }: { type: InquiryType }) {
  const config = TYPE_CONFIG[type];
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ${config.className}`}
    >
      <span className="text-[10px]">{config.icon}</span>
      {config.label}
    </span>
  );
}

function CreateInquiryDialog() {
  const [open, setOpen] = useState(false);
  const [type, setType] = useState<InquiryType>('general');
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [visibility, setVisibility] = useState<'private' | 'public'>('private');

  const { mutate: createInquiry, isPending } = useCreateInquiry();

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    createInquiry(
      { type, title, content, visibility },
      {
        onSuccess: () => {
          setOpen(false);
          setTitle('');
          setContent('');
          setType('general');
          setVisibility('private');
        },
      },
    );
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className="rounded-xl font-semibold active:scale-95 transition-transform">
          <Plus className="w-4 h-4 mr-1.5" />
          문의 작성
        </Button>
      </DialogTrigger>
      <DialogContent className="rounded-2xl">
        <DialogHeader>
          <DialogTitle>새 문의 작성</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label>유형</Label>
            <div className="flex gap-2">
              {(['general', 'bug_report', 'feature_request'] as const).map((t) => (
                <button
                  key={t}
                  type="button"
                  onClick={() => setType(t)}
                  className={`flex items-center gap-1.5 rounded-xl px-3 py-2 text-sm font-medium transition-all active:scale-95 ${
                    type === t
                      ? 'bg-primary text-primary-foreground shadow-sm'
                      : 'bg-muted text-muted-foreground hover:text-foreground'
                  }`}
                >
                  <span className="text-xs">{TYPE_CONFIG[t].icon}</span>
                  {TYPE_CONFIG[t].label}
                </button>
              ))}
            </div>
          </div>
          <div className="space-y-2">
            <Label>제목</Label>
            <Input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="문의 제목을 입력하세요"
              required
            />
          </div>
          <div className="space-y-2">
            <Label>내용</Label>
            <textarea
              value={content}
              onChange={(e) => setContent(e.target.value)}
              placeholder="문의 내용을 자세히 입력해주세요"
              required
              rows={5}
              className="flex w-full border-none bg-muted rounded-xl px-3 py-2.5 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary resize-none"
            />
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setVisibility(visibility === 'private' ? 'public' : 'private')}
              className="flex items-center gap-1.5 rounded-xl bg-muted px-3 py-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
            >
              {visibility === 'private' ? (
                <>
                  <EyeOff className="w-3.5 h-3.5" /> 비공개
                </>
              ) : (
                <>
                  <Eye className="w-3.5 h-3.5" /> 공개
                </>
              )}
            </button>
          </div>
          <Button
            type="submit"
            disabled={isPending || !title.trim() || !content.trim()}
            className="w-full rounded-xl font-semibold active:scale-95 transition-transform"
          >
            {isPending ? '작성 중...' : '문의 등록'}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function FilterPill({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`rounded-full px-3.5 py-1.5 text-xs font-medium transition-all active:scale-95 ${
        active
          ? 'bg-foreground text-background shadow-sm'
          : 'bg-muted text-muted-foreground hover:text-foreground'
      }`}
    >
      {children}
    </button>
  );
}

function SupportListPage() {
  const { mallId } = useParams({ from: '/_authenticated/$mallId/support/' });
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [visibilityTab, setVisibilityTab] = useState<'all' | 'public' | 'private'>('all');

  const { data, isLoading, isError } = useInquiries({
    page,
    limit,
    type: typeFilter !== 'all' ? (typeFilter as InquiryType) : undefined,
    status: statusFilter !== 'all' ? (statusFilter as InquiryStatus) : undefined,
    visibility: visibilityTab !== 'all' ? visibilityTab : undefined,
  });

  return (
    <AppLayout pageTitle="문의 관리">
      <AnimatedContainer>
        <div className="space-y-6">
          <PageHeader
            title="문의 게시판"
            description="서비스 이용 중 궁금한 점이나 불편한 점을 남겨주세요"
            action={<CreateInquiryDialog />}
          />

          {/* 공개/비공개 탭 */}
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <FilterPill
                active={visibilityTab === 'all'}
                onClick={() => {
                  setVisibilityTab('all');
                  setPage(1);
                }}
              >
                전체
              </FilterPill>
              <FilterPill
                active={visibilityTab === 'public'}
                onClick={() => {
                  setVisibilityTab('public');
                  setPage(1);
                }}
              >
                <Globe className="w-3 h-3 mr-1 inline" />
                공개 문의
              </FilterPill>
              <FilterPill
                active={visibilityTab === 'private'}
                onClick={() => {
                  setVisibilityTab('private');
                  setPage(1);
                }}
              >
                <Lock className="w-3 h-3 mr-1 inline" />내 문의
              </FilterPill>
            </div>
            <p className="text-xs text-muted-foreground/70">
              {visibilityTab === 'public'
                ? '공개 문의는 다른 사용자들도 볼 수 있어요'
                : visibilityTab === 'private'
                  ? '비공개 문의는 나와 관리자만 볼 수 있어요'
                  : '공개 문의와 내 비공개 문의를 함께 볼 수 있어요'}
            </p>
          </div>

          {/* 필터 — pill 스타일 */}
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <FilterPill
                active={typeFilter === 'all'}
                onClick={() => {
                  setTypeFilter('all');
                  setPage(1);
                }}
              >
                전체
              </FilterPill>
              <FilterPill
                active={typeFilter === 'general'}
                onClick={() => {
                  setTypeFilter('general');
                  setPage(1);
                }}
              >
                💬 일반 문의
              </FilterPill>
              <FilterPill
                active={typeFilter === 'bug_report'}
                onClick={() => {
                  setTypeFilter('bug_report');
                  setPage(1);
                }}
              >
                🐛 버그 신고
              </FilterPill>
              <FilterPill
                active={typeFilter === 'feature_request'}
                onClick={() => {
                  setTypeFilter('feature_request');
                  setPage(1);
                }}
              >
                💡 기능 요청
              </FilterPill>
            </div>
            <div className="flex items-center gap-2">
              <FilterPill
                active={statusFilter === 'all'}
                onClick={() => {
                  setStatusFilter('all');
                  setPage(1);
                }}
              >
                전체 상태
              </FilterPill>
              {(['submitted', 'reviewing', 'answered', 'resolved'] as const).map((s) => (
                <FilterPill
                  key={s}
                  active={statusFilter === s}
                  onClick={() => {
                    setStatusFilter(s);
                    setPage(1);
                  }}
                >
                  {STATUS_CONFIG[s].label}
                </FilterPill>
              ))}
            </div>
          </div>

          {/* 목록 */}
          {isLoading ? (
            <div className="space-y-3">
              {Array.from({ length: 5 }).map((_, i) => (
                <Skeleton key={i} className="h-24 rounded-2xl" />
              ))}
            </div>
          ) : isError ? (
            <div className="text-center py-8">
              <p className="text-sm text-muted-foreground">문의 목록을 불러오지 못했어요</p>
            </div>
          ) : !data?.data.length ? (
            <Card className="rounded-2xl">
              <CardContent className="flex flex-col items-center justify-center py-16">
                <MessageSquare className="w-12 h-12 text-muted-foreground/30 mb-3" />
                <p className="text-muted-foreground text-sm">아직 작성된 문의가 없어요</p>
                <p className="text-muted-foreground/60 text-xs mt-1">
                  궁금한 점이 있다면 문의를 남겨주세요
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              {data.data.map((inquiry) => (
                <Link
                  key={inquiry.id}
                  to="/$mallId/support/$inquiryId"
                  params={{ mallId: mallId, inquiryId: inquiry.id }}
                  className="block"
                >
                  <Card className="rounded-2xl hover:shadow-md transition-all cursor-pointer active:scale-[0.995]">
                    <CardContent className="p-5">
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1.5">
                            <TypeBadge type={inquiry.type} />
                            <Badge variant={STATUS_CONFIG[inquiry.status].variant}>
                              {STATUS_CONFIG[inquiry.status].label}
                            </Badge>
                            {inquiry.visibility === 'private' ? (
                              <span className="inline-flex items-center gap-0.5 text-xs text-muted-foreground/50">
                                <EyeOff className="w-3 h-3" />
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-0.5 text-xs text-muted-foreground/50">
                                <Eye className="w-3 h-3" />
                              </span>
                            )}
                            {inquiry.mallId !== mallId && (
                              <span className="text-[10px] text-muted-foreground/40">
                                다른 사용자
                              </span>
                            )}
                          </div>
                          <h3 className="font-semibold text-sm truncate">{inquiry.title}</h3>
                          <p className="text-xs text-muted-foreground mt-1 line-clamp-1">
                            {inquiry.content}
                          </p>
                        </div>
                        <span className="text-xs text-muted-foreground whitespace-nowrap tabular-nums">
                          {new Date(inquiry.createdAt).toLocaleDateString('ko-KR')}
                        </span>
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          )}

          {/* 페이지네이션 */}
          {data?.pagination && (
            <DataTablePagination
              page={page}
              limit={limit}
              total={data.pagination.total}
              totalPages={Math.ceil(data.pagination.total / limit)}
              onPageChange={setPage}
              onLimitChange={setLimit}
            />
          )}
        </div>
      </AnimatedContainer>
    </AppLayout>
  );
}
